import { useState, useEffect } from 'react';
import { Icon, TextField } from '@scale/scaleui';
import BuilderHint from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderHint';
import _ from 'lodash';

interface KeyValuePairsBuilderProps {
  name: string;
  existingValue?: Record<string, string>;
  updateProperty: (value: Record<string, string>) => void;
  required?: boolean;
  hint?: string;
}

const KeyValuePairsBuilder = ({
  existingValue,
  updateProperty,
  name,
  required,
  hint,
}: KeyValuePairsBuilderProps) => {
  const [keyValuePairs, setKeyValuePairs] = useState<{ key: string; value: string }[]>(
    existingValue
      ? Object.keys(existingValue).map(key => ({ key, value: existingValue[key] }))
      : [],
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const newPairs: Record<string, string> = {};
    keyValuePairs.forEach(pair => {
      newPairs[pair.key] = pair.value;
    });
    if (_.isEqual(newPairs, existingValue)) {
      return;
    }
    updateProperty(newPairs);
  }, [existingValue, keyValuePairs, updateProperty]);

  useEffect(() => {
    const anyKeysOrValuesEmpty = keyValuePairs.some(
      pair => !(pair.key.length && pair.value.length),
    );
    if (anyKeysOrValuesEmpty) {
      setErrorMessage('Key or value pairs cannot be empty.');
    } else {
      setErrorMessage('');
    }
  }, [keyValuePairs]);

  return (
    <div className="mt-4 mb-8">
      <div className="flex flex-row">
        <label className="block text-sm font-medium leading-6 text-neutral-700">
          {name}
          {required && <span className="text-danger-400">*</span>}
        </label>
        {hint && <BuilderHint name={name} hint={hint} />}
        <Icon
          hasBackground
          size="sm"
          icon="plus"
          variant="info"
          onClick={() => {
            const newPairs = [...keyValuePairs];
            newPairs.push({ key: '', value: '' });
            setKeyValuePairs(newPairs);
          }}
          className="ml-auto"
        />
      </div>
      {keyValuePairs.map((pair, index) => (
        <div key={index} className="flex flex-row">
          <TextField
            id="key"
            value={pair.key}
            onChange={e => {
              const newPairs = [...keyValuePairs];
              newPairs[index].key = e.target.value;
              setKeyValuePairs(newPairs);
            }}
            className="w-[70%] mr-2"
            placeholder="Key"
          />
          <TextField
            id="value"
            value={pair.value}
            onChange={e => {
              const newPairs = [...keyValuePairs];
              newPairs[index].value = e.target.value;
              setKeyValuePairs(newPairs);
            }}
            className="w-[30%] mr-2"
            placeholder="Value"
          />
          <Icon
            hasBackground
            size="sm"
            icon="trash"
            variant="danger"
            className="mt-2"
            onClick={() => {
              const newPairs = [...keyValuePairs];
              newPairs.splice(index, 1);
              setKeyValuePairs(newPairs);
            }}
          />
        </div>
      ))}
      <p className="text-danger-400">{errorMessage}</p>
    </div>
  );
};

export default KeyValuePairsBuilder;
